import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { SummaryCardComponent } from '@next/core-lib/components';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TenantAwareTranslationService } from 'src/app/shared/services/tenant-aware-translation.service';
import { ALLOWED_BENCHMARK_COUNTRIES } from 'src/assets/data/AllowedBenchmarkCountries';

import {
  BenchmarkSettings,
  BenchmarkTypes,
  FarmerInsightsModuleState,
} from '../../models/farmer-insights.model';

export interface BenchmarkSettingsDialogInputData extends AbstractFormDialogData {
  settings: BenchmarkSettings;
}

export interface BenchmarkSettingsResultData {
  settings: BenchmarkSettings;
}

@Component({
  selector: 'app-benchmark-settings',
  templateUrl: './benchmark-settings.component.html',
  styleUrls: ['./benchmark-settings.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    SummaryCardComponent,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    NgIf,
    MatIcon,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class BenchmarkSettingsComponent extends AbstractFormDialog<BenchmarkSettingsResultData> {
  data = inject<BenchmarkSettingsDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        BenchmarkSettingsComponent,
        AbstractDialogResultData<BenchmarkSettingsResultData>
      >
    >(MatDialogRef);
  private localeService = inject(LocaleService);
  private readonly tenantTranslation = inject(TenantAwareTranslationService);
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);

  @Input() formGroup: FormGroup;

  readonly translateLabel = (key: string): string => this.tenantTranslation.translate(key);

  public showAgrifirmBenchmarks$: Observable<boolean> = this.store
    .select(UserSelectors.selectLocation)
    .pipe(map((l) => ALLOWED_BENCHMARK_COUNTRIES.includes(l.country)));

  public benchmarkOptions$: Observable<{ label: string; value: string }[]> =
    this.showAgrifirmBenchmarks$.pipe(
      map((showBenchmark) => {
        const options = [
          {
            label: 'farmer-insights.settings.none',
            value: BenchmarkTypes.None,
          },
        ];

        if (showBenchmark) {
          options.push(
            {
              label: this.tenantTranslation.translate(
                'farmer-insights.settings.selected-period-label',
              ),
              value: BenchmarkTypes.SelectedPeriod,
            },
            {
              label: this.tenantTranslation.translate('farmer-insights.settings.last-weeks-label'),
              value: BenchmarkTypes.LastWeeks,
            },
          );
        }

        options.push({
          label: 'farmer-insights.settings.customer-average-selected-period-label',
          value: BenchmarkTypes.CustomerAverageSelectedPeriod,
        });

        return options;
      }),
    );

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    const data = this.data;

    this.data.title = this.localeService.translate('farmer-insights.settings.header');

    this.formGroup = new FormGroup({
      nonDetailedBenchmark: new FormControl(data.settings.selectedNonDetailedBenchmarkType),
      lastWeeks: new FormControl(data.settings.lastWeeks),
      selectedPeriod: new FormControl(data.settings.selectedPeriod),
      customerAverageSelectedPeriod: new FormControl(data.settings.customerAverageSelectedPeriod),
    });
  }

  save() {
    const settingsResponse = {
      settings: {
        selectedNonDetailedBenchmarkType: this.formGroup.controls['nonDetailedBenchmark']
          .value as BenchmarkTypes,
        selectedPeriod: this.formGroup.controls['selectedPeriod'].value as boolean,
        lastWeeks: this.formGroup.controls['lastWeeks'].value as boolean,
        customerAverageSelectedPeriod: this.formGroup.controls['customerAverageSelectedPeriod']
          .value as boolean,
      },
    };
    this.confirm(settingsResponse);
  }
}
